/**
 * fieldMappingApply — the one place a Field Mappings set is applied to a
 * record (Settings → Field Mappings; connector_configs.field_mappings).
 *
 * Pure and deterministic. Import: the external values of one record, keyed by
 * the connector's external field keys, go through the set's rules and come out
 * as item fields plus attribute values — each remembering the rule and the
 * external value that produced it (canon: no accepted value without
 * provenance). Export runs the same rules backwards through the same value
 * lists. Every connector uses these functions; none keeps its own copy.
 * The note every set-written value carries, the summary wording, and
 * acceptMappedAttribute (which writes through the accept function it is
 * given) are shared by an import and by applying a set later.
 */

import { humanizeAttributeKey } from './sessionCriteriaFromTrack';
import {
  CONNECTOR_FIELDS,
  INTERNAL_FIELDS,
  type FieldMappingConfig,
  type FieldMappingEntry,
  type FieldMappingSet,
} from './fieldMappingRegistry';

export type MappingFlow = 'import' | 'export';

/** Prefix that marks a rule's target as a workspace attribute (`attribute:<key>`). */
export const ATTRIBUTE_TARGET_PREFIX = 'attribute:';

/** The attribute key a target names, or null when the target is not an attribute. */
export function attributeKeyOfTarget(internalFieldKey: string): string | null {
  if (!internalFieldKey.startsWith(ATTRIBUTE_TARGET_PREFIX)) return null;
  const key = internalFieldKey.slice(ATTRIBUTE_TARGET_PREFIX.length).trim();
  return key || null;
}

/** Prefix that marks a rule's target as one of the workspace's custom fields (`custom_field:<key>`). */
export const CUSTOM_FIELD_TARGET_PREFIX = 'custom_field:';

/** The custom field key a target names, or null when the target is not a custom field. */
export function customFieldKeyOfTarget(internalFieldKey: string): string | null {
  if (!internalFieldKey.startsWith(CUSTOM_FIELD_TARGET_PREFIX)) return null;
  const key = internalFieldKey.slice(CUSTOM_FIELD_TARGET_PREFIX.length).trim();
  return key || null;
}

/** True when a rule is switched on, complete, and runs in this direction. */
export function ruleRunsOn(entry: FieldMappingEntry, flow: MappingFlow): boolean {
  return (
    entry.enabled &&
    !!entry.externalFieldKey &&
    !!entry.internalFieldKey &&
    (entry.direction === flow || entry.direction === 'both')
  );
}

// ── Which set applies ─────────────────────────────────────────────────────────

export interface ActiveMappingSet {
  set: FieldMappingSet | null;
  /** True when someone picked this set on Field Mappings; false when it was assumed. */
  chosen: boolean;
}

/**
 * The set that applies to an import (or export): the one picked on Field
 * Mappings. When none is picked, the first set with an enabled rule for that
 * direction — so a set someone built but never picked still runs.
 */
export function resolveActiveMappingSet(
  config: FieldMappingConfig,
  flow: MappingFlow = 'import',
): ActiveMappingSet {
  const pickedId = flow === 'import' ? config.importSetId : config.exportSetId;
  const picked = pickedId ? config.sets.find((s) => s.id === pickedId) : undefined;
  if (picked) return { set: picked, chosen: true };
  const assumed = config.sets.find((s) => s.entries.some((e) => ruleRunsOn(e, flow)));
  return { set: assumed ?? null, chosen: false };
}

// ── Import ────────────────────────────────────────────────────────────────────

/** One value a rule produced, with where it came from. */
export interface MappedValue {
  /** Registry key of the Priorities.ai field, e.g. 'title' or 'attribute:importance'. */
  internalFieldKey: string;
  value: unknown;
  ruleId: string;
  externalFieldKey: string;
  externalValue: unknown;
  /** True when the rule's value list translated the external value. */
  viaValueMap: boolean;
  /** The attribute scale the rule writes to, when it names one. */
  scaleId?: string;
}

export interface MappedAttributeValue extends MappedValue {
  attributeKey: string;
}

/** A rule that had a value to read, but its value list has no entry for it. */
export interface UnmatchedValue {
  ruleId: string;
  externalFieldKey: string;
  internalFieldKey: string;
  externalValue: unknown;
}

export interface MappingSetApplication {
  /** Values for item fields (everything that is not an attribute). */
  fields: MappedValue[];
  /** Values for workspace attributes, written after the item exists. */
  attributes: MappedAttributeValue[];
  unmatched: UnmatchedValue[];
}

/** No value: missing, empty text, or a list of nothing. */
export function isBlank(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.every(isBlank));
}

/** A rule reads the free-text name it was given (e.g. a Notion property), else its key. */
function sourceValue(external: Record<string, unknown>, entry: FieldMappingEntry): unknown {
  const label = entry.externalFieldLabel?.trim();
  if (label && Object.prototype.hasOwnProperty.call(external, label)) return external[label];
  return external[entry.externalFieldKey];
}

/**
 * Run one record's external values through a set's import rules. Disabled,
 * export-only and half-filled rules are ignored; a rule whose source value is
 * missing produces nothing; a rule with a value list produces a value only for
 * external values on that list (the rest are reported as unmatched).
 *
 * An external value can be a list (a sticky's Miro tags). A list target (Cue /
 * Tag) takes every element — through the value list when the rule has one,
 * dropping and reporting the elements it does not name. Any other target takes
 * one element: the first the value list names, in the record's order, or the
 * first element when the rule has no value list.
 */
export function applyMappingSet(
  external: Record<string, unknown>,
  set: FieldMappingSet | null | undefined,
): MappingSetApplication {
  const out: MappingSetApplication = { fields: [], attributes: [], unmatched: [] };
  for (const entry of set?.entries ?? []) {
    if (!ruleRunsOn(entry, 'import')) continue;
    const raw = sourceValue(external, entry);
    if (isBlank(raw)) continue;

    const viaValueMap = !!entry.valueMap?.length;
    const lookup = (v: unknown) => entry.valueMap?.find((m) => m.externalValue === String(v));
    const unmatchedOf = (v: unknown): UnmatchedValue => ({
      ruleId: entry.id,
      externalFieldKey: entry.externalFieldKey,
      internalFieldKey: entry.internalFieldKey,
      externalValue: v,
    });

    let externalValue: unknown = raw;
    let value: unknown = raw;
    if (Array.isArray(raw)) {
      const elements = raw.filter((v) => !isBlank(v));
      const named = viaValueMap ? elements.filter((v) => lookup(v)) : elements;
      if (ITEM_CREATE_TARGETS[entry.internalFieldKey]?.list) {
        if (viaValueMap) out.unmatched.push(...elements.filter((v) => !lookup(v)).map(unmatchedOf));
        if (named.length === 0) continue;
        externalValue = named;
        value = viaValueMap ? named.map((v) => lookup(v)?.internalValue) : named;
      } else {
        if (named.length === 0) {
          out.unmatched.push(...elements.map(unmatchedOf));
          continue;
        }
        externalValue = named[0];
        value = viaValueMap ? lookup(externalValue)?.internalValue : externalValue;
      }
    } else if (viaValueMap) {
      const hit = lookup(raw);
      if (!hit) {
        out.unmatched.push(unmatchedOf(raw));
        continue;
      }
      value = hit.internalValue;
    }

    const mapped: MappedValue = {
      internalFieldKey: entry.internalFieldKey,
      value,
      ruleId: entry.id,
      externalFieldKey: entry.externalFieldKey,
      externalValue,
      viaValueMap,
      ...(entry.scaleId ? { scaleId: entry.scaleId } : {}),
    };
    const attributeKey = attributeKeyOfTarget(entry.internalFieldKey);
    if (attributeKey) out.attributes.push({ ...mapped, attributeKey });
    else out.fields.push(mapped);
  }
  // Several rules may read one list field, each naming part of it (Linear labels
  // "Impact: 9" for a 1–10 scale, "Impact: High" for Low / Medium / High). A value
  // another rule on the same field names is not unmatched.
  out.unmatched = out.unmatched.filter(
    (u) =>
      !(set?.entries ?? []).some(
        (e) =>
          e.id !== u.ruleId &&
          ruleRunsOn(e, 'import') &&
          e.externalFieldKey === u.externalFieldKey &&
          !!e.valueMap?.some((m) => m.externalValue === String(u.externalValue)),
      ),
  );
  return out;
}

// ── Mapped fields → a new item ────────────────────────────────────────────────

function enumOf(internalFieldKey: string): ReadonlySet<string> {
  return new Set(INTERNAL_FIELDS.find((f) => f.key === internalFieldKey)?.enumValues?.map((v) => v.value) ?? []);
}

/**
 * Where a mapped field lands on an item being created by an import. Only the
 * fields every import path already sets on create; the rest are reported as
 * "can't be set on import". `category` follows the visual-language convention
 * (whiteboardSignifiers): it is the item's category, so it takes the same
 * values as Item Language Type.
 */
const ITEM_CREATE_TARGETS: Record<string, { field: string; allowed?: ReadonlySet<string>; list?: true }> = {
  title: { field: 'title' },
  description: { field: 'description' },
  item_language_type: { field: 'itemCategory', allowed: enumOf('item_language_type') },
  category: { field: 'itemCategory', allowed: enumOf('item_language_type') },
  altitude: { field: 'altitudeLevel', allowed: enumOf('altitude') },
  strategic_bucket: { field: 'strategicBucket', allowed: enumOf('strategic_bucket') },
  cue: { field: 'tags', list: true },
};

export type SkippedFieldReason = 'not_settable_on_import' | 'value_not_allowed';

export interface ItemCreateFields {
  /** Spread onto the create call: title, description, itemCategory, altitudeLevel, strategicBucket. */
  fields: Record<string, string>;
  /** Tags to add (from Cue / Tag rules). */
  tags: string[];
  /** Custom field values, keyed by field key — they live in the item's metadata. */
  metadata: Record<string, unknown>;
  skipped: Array<{ mapped: MappedValue; reason: SkippedFieldReason }>;
}

function asList(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : String(value).split(',');
  return raw.map((v) => String(v).trim()).filter(Boolean);
}

/** Fold mapped field values into what an import may set on a new item. */
export function itemCreateFieldsFromMapping(fields: MappedValue[]): ItemCreateFields {
  const out: ItemCreateFields = { fields: {}, tags: [], metadata: {}, skipped: [] };
  for (const mapped of fields) {
    const customFieldKey = customFieldKeyOfTarget(mapped.internalFieldKey);
    if (customFieldKey) {
      out.metadata[customFieldKey] = mapped.value;
      continue;
    }
    const target = ITEM_CREATE_TARGETS[mapped.internalFieldKey];
    if (!target) {
      out.skipped.push({ mapped, reason: 'not_settable_on_import' });
      continue;
    }
    if (target.list) {
      for (const tag of asList(mapped.value)) if (!out.tags.includes(tag)) out.tags.push(tag);
      continue;
    }
    const value = String(mapped.value).trim();
    if (!value) continue;
    if (target.allowed && !target.allowed.has(value)) {
      out.skipped.push({ mapped, reason: 'value_not_allowed' });
      continue;
    }
    out.fields[target.field] = value;
  }
  return out;
}

/** The item's own value for a structural target (the reverse of ITEM_CREATE_TARGETS). */
export function itemValueForTarget(item: Record<string, unknown>, internalFieldKey: string): unknown {
  const customFieldKey = customFieldKeyOfTarget(internalFieldKey);
  if (customFieldKey) return (item.metadata as Record<string, unknown> | undefined)?.[customFieldKey];
  const target = ITEM_CREATE_TARGETS[internalFieldKey];
  return target ? item[target.field] : undefined;
}

// ── Export ────────────────────────────────────────────────────────────────────

export interface ExportedValue {
  externalFieldKey: string;
  value: unknown;
  ruleId: string;
  internalFieldKey: string;
  internalValue: unknown;
  viaValueMap: boolean;
}

/** Same value however it was stored: '8' and 8 match. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (String(a).trim() === String(b).trim()) return true;
  const x = Number(a);
  const y = Number(b);
  return String(a).trim() !== '' && String(b).trim() !== '' && Number.isFinite(x) && Number.isFinite(y) && x === y;
}

/**
 * Run one record's internal values (keyed by internal field key, e.g.
 * 'attribute:importance') backwards through a set's export rules. A value list
 * is read right to left: the first external value whose internal value matches
 * wins. Returns one value per external field (first rule wins).
 */
export function exportThroughMappingSet(
  internal: Record<string, unknown>,
  set: FieldMappingSet | null | undefined,
): Record<string, ExportedValue> {
  const out: Record<string, ExportedValue> = {};
  for (const entry of set?.entries ?? []) {
    if (!ruleRunsOn(entry, 'export')) continue;
    if (out[entry.externalFieldKey]) continue;
    const internalValue = internal[entry.internalFieldKey];
    if (isBlank(internalValue)) continue;
    let value: unknown = internalValue;
    let viaValueMap = false;
    if (entry.valueMap?.length) {
      const hit = entry.valueMap.find((v) => sameValue(v.internalValue, internalValue));
      if (!hit) continue;
      value = hit.externalValue;
      viaValueMap = true;
    }
    out[entry.externalFieldKey] = {
      externalFieldKey: entry.externalFieldKey,
      value,
      ruleId: entry.id,
      internalFieldKey: entry.internalFieldKey,
      internalValue,
      viaValueMap,
    };
  }
  return out;
}

// ── Plain-words descriptions ──────────────────────────────────────────────────

/** "Sticky Note Color", or the free-text name the rule was given. */
export function externalFieldLabel(connectorType: string, entry: Pick<FieldMappingEntry, 'externalFieldKey' | 'externalFieldLabel'>): string {
  const def = CONNECTOR_FIELDS[connectorType]?.find((f) => f.key === entry.externalFieldKey);
  return entry.externalFieldLabel?.trim() || def?.label || entry.externalFieldKey;
}

/** "Yellow" for 'yellow' on Sticky Note Color; the raw value when it has no label. */
export function externalValueLabel(connectorType: string, externalFieldKey: string, value: unknown): string {
  const def = CONNECTOR_FIELDS[connectorType]?.find((f) => f.key === externalFieldKey);
  const hit = def?.enumValues?.find((v) => v.value === String(value));
  return hit?.label ?? String(value);
}

/** "Importance" for 'attribute:importance' (the workspace's label when it has one). */
export function internalFieldLabel(
  internalFieldKey: string,
  attributeLabel?: (attributeKey: string) => string | undefined,
): string {
  const attributeKey = attributeKeyOfTarget(internalFieldKey) ?? customFieldKeyOfTarget(internalFieldKey);
  if (attributeKey) return attributeLabel?.(attributeKey) ?? humanizeAttributeKey(attributeKey);
  return INTERNAL_FIELDS.find((f) => f.key === internalFieldKey)?.label ?? internalFieldKey;
}

/**
 * What a set does, in one line: "Sticky Note Color → Importance (Yellow → 10,
 * Orange → 8, Purple → 4)". Rules are separated by "; ".
 */
export function describeMappingSetRules(
  set: FieldMappingSet,
  connectorType: string,
  flow: MappingFlow = 'import',
  attributeLabel?: (attributeKey: string) => string | undefined,
): string {
  return set.entries
    .filter((e) => ruleRunsOn(e, flow))
    .map((e) => {
      const head = `${externalFieldLabel(connectorType, e)} → ${internalFieldLabel(e.internalFieldKey, attributeLabel)}`;
      if (!e.valueMap?.length) return head;
      const pairs = e.valueMap
        .map((v) => `${externalValueLabel(connectorType, e.externalFieldKey, v.externalValue)} → ${v.internalValue}`)
        .join(', ');
      return `${head} (${pairs})`;
    })
    .join('; ');
}

// ── Provenance notes ──────────────────────────────────────────────────────────

/**
 * Every accepted value a mapping set writes carries a note that starts with
 * this. Applying a set to items already imported changes only values whose
 * note starts with it; a value a person set is left alone.
 */
export const MAPPING_NOTE_PREFIX = 'Field mapping';

/** True when an accepted value's note says a mapping set wrote it. */
export function writtenByMapping(note: string | null | undefined): boolean {
  return !!note && note.startsWith(MAPPING_NOTE_PREFIX);
}

/**
 * The system and record a linked-item sync brought a value from, read from the
 * note it wrote ("Field mapping at Linear sync of PRI-6 — …"); null for any other value.
 */
export function syncedFrom(note: string | null | undefined): { system: string; record: string } | null {
  const m = note?.match(/^Field mapping at (.+?) sync of (\S+)/);
  return m ? { system: m[1], record: m[2] } : null;
}

/**
 * "Field mapping at Miro import — Sticky Note Color “Yellow” → 10 (mapping set
 * “Default”)". `when` names the act that wrote it ('at Miro import', 'applied later').
 */
export function mappingValueNote(when: string, connectorType: string, mapped: MappedValue, setName: string): string {
  const field = externalFieldLabel(connectorType, mapped);
  const shown = externalValueLabel(connectorType, mapped.externalFieldKey, mapped.externalValue);
  const arrow = mapped.viaValueMap ? ` → ${String(mapped.value)}` : '';
  return `${MAPPING_NOTE_PREFIX} ${when} — ${field} “${shown}”${arrow} (mapping set “${setName}”)`;
}

// ── Writing a mapped attribute value ──────────────────────────────────────────

/** valueProposalStore.acceptValueDirectly, as every run of a set calls it. */
export type AcceptDirectly = (params: {
  itemId: string;
  attributeKey: string;
  scaleId?: string;
  value: unknown;
  acceptedBy: string;
  acceptanceMethod: 'authority';
  acceptanceNote: string;
}) => Promise<{ saved: boolean; error?: string }>;

/**
 * Write one mapped attribute value as an authority-accepted value carrying its
 * note. True when it saved; a refusal is counted in `problems` with its reason.
 */
export async function acceptMappedAttribute(
  deps: { acceptedBy: string | null; accept: AcceptDirectly },
  itemId: string,
  mapped: MappedAttributeValue,
  note: string,
  problems: MappingProblems,
): Promise<boolean> {
  if (!deps.acceptedBy) {
    countFailure(problems, 'No one is signed in to accept the values');
    return false;
  }
  try {
    const result = await deps.accept({
      itemId,
      attributeKey: mapped.attributeKey,
      ...(mapped.scaleId ? { scaleId: mapped.scaleId } : {}),
      value: mapped.value,
      acceptedBy: deps.acceptedBy,
      acceptanceMethod: 'authority',
      acceptanceNote: note,
    });
    if (!result.saved) countFailure(problems, result.error);
    return result.saved;
  } catch (err) {
    countFailure(problems, err instanceof Error ? err.message : undefined);
    return false;
  }
}

// ── What a run did, in plain words ────────────────────────────────────────────
// One tally and one set of sentences for every run of a set: an import, and
// applying a set to items already imported.

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function bump(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) ?? 0) + 1);
}

/** What a run could not do, counted as it goes. */
export interface MappingProblems {
  /** Attribute key → values skipped because the workspace has no such attribute. */
  missingAttribute: Map<string, number>;
  /** External value label → how often a value list had no entry for it. */
  unmatched: Map<string, number>;
  notSettable: Map<string, number>;
  notAllowed: Map<string, number>;
  failed: number;
  firstFailure: string;
}

export function newMappingProblems(): MappingProblems {
  return { missingAttribute: new Map(), unmatched: new Map(), notSettable: new Map(), notAllowed: new Map(), failed: 0, firstFailure: '' };
}

export function countMissingAttribute(p: MappingProblems, attributeKey: string): void {
  bump(p.missingAttribute, attributeKey);
}

export function countUnmatched(p: MappingProblems, connectorType: string, unmatched: UnmatchedValue[]): void {
  for (const u of unmatched) bump(p.unmatched, externalValueLabel(connectorType, u.externalFieldKey, u.externalValue));
}

export function countSkippedFields(p: MappingProblems, skipped: ItemCreateFields['skipped']): void {
  for (const s of skipped) {
    const label = internalFieldLabel(s.mapped.internalFieldKey);
    if (s.reason === 'not_settable_on_import') bump(p.notSettable, label);
    else bump(p.notAllowed, `${label} “${String(s.mapped.value)}”`);
  }
}

export function countFailure(p: MappingProblems, reason: string | undefined): void {
  p.failed++;
  p.firstFailure ||= reason || 'The value was not saved';
}

/** The sentences that follow a run's headline; `hasProblems` when something was skipped or not saved. */
export function describeMappingProblems(p: MappingProblems): { sentences: string[]; hasProblems: boolean } {
  const list = (m: Map<string, number>) => [...m].map(([l, n]) => `${l} (${n})`).join(', ');
  const sentences: string[] = [];
  for (const [key, n] of p.missingAttribute) {
    sentences.push(`Skipped ${plural(n, `“${key}” value`)} — this workspace has no attribute called “${key}”.`);
  }
  if (p.failed > 0) {
    sentences.push(`${plural(p.failed, 'value')} could not be saved: ${p.firstFailure.trim().replace(/\.$/, '')}.`);
  }
  if (p.notSettable.size > 0) sentences.push(`Not set, because a mapping set can't set these fields: ${list(p.notSettable)}.`);
  if (p.notAllowed.size > 0) sentences.push(`Not set, because the field doesn't take that value: ${list(p.notAllowed)}.`);
  if (p.unmatched.size > 0) sentences.push(`No value in the set for: ${list(p.unmatched)}.`);
  return {
    sentences,
    hasProblems: p.missingAttribute.size > 0 || p.failed > 0 || p.notSettable.size > 0 || p.notAllowed.size > 0,
  };
}
